/**
 * useShellAttackProfiles - the attacker's "profile list", DERIVED from the roster.
 *
 * Under the unified model you can act as any combatant, so the profile list
 * is the roster itself. `atkProfiles` / `localCurrentProfile` are LEGACY
 * SHIMS (engine/roster/legacyAdapter) so AttackerPanel's dropdown keeps
 * working untouched. A SCRATCH grid serves "not acting as anyone yet": a
 * fresh session still lets you hand-build a weapon without first loading a
 * character. Local and unsynced, like the old Default profile; weapons
 * acquire an owner the moment you act as someone.
 *
 * Also drops the armed weapon when the actor changes to someone with no such
 * plate: `selectedSlot` is an index, and index 3 carried onto a two-weapon
 * marine would silently arm his chainsword. Keyed on the ACTOR, not on the
 * rack - a rack edit already remaps the selection itself.
 *
 * Returns `{ atkProfiles, localCurrentProfile, setAtkProfiles,
 * importAtkProfiles, currentSlots }`. Extracted from DeathWatchSimulator.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { blankProfile } from '../engine/factories';
import { applyLegacyAtkProfiles, NO_ACTOR_PROFILE, toLegacyAtkProfiles } from '../engine/roster/legacyAdapter';
import { projectLoadout } from '../engine/roster/projection';
import { migrateSessionV1toV2 } from '../engine/roster/migration';

const resolveUpdater = (updater, current) => (typeof updater === 'function' ? updater(current) : updater);

/** Acting as nobody: the profile update lands on the scratch grid. */
export function nextScratch(roster, prevScratch, updater) {
  const next = resolveUpdater(updater, toLegacyAtkProfiles(roster, null, prevScratch));
  return next.list?.[NO_ACTOR_PROFILE] ?? prevScratch;
}

/** Acting as someone: the profile update lands on their combatant. */
function nextRoster(prev, actorId, scratchSlots, updater) {
  return applyLegacyAtkProfiles(
    prev,
    actorId,
    resolveUpdater(updater, toLegacyAtkProfiles(prev, actorId, scratchSlots)),
  );
}

/** Legacy session import: profiles + _charBindings become combatants, and the actor moves to theirs. */
function useImportAtkProfiles(setRoster, setActorId) {
  return useCallback(
    (profiles) => {
      const { combatants, actorId: importedActor } = migrateSessionV1toV2({ atkProfiles: profiles });
      if (!combatants.length) {
        setActorId(null);
        return;
      }
      setRoster((prev) => [...prev, ...combatants]);
      if (importedActor) setActorId(importedActor);
    },
    [setRoster, setActorId],
  );
}

/** Drop a stale selection when the actor changes. The rack is read as it stands in that render. */
function useDropStaleSelection(actorId, currentSlots, setSelectedSlot) {
  const slotsRef = useRef(currentSlots);
  slotsRef.current = currentSlots;
  useEffect(() => {
    setSelectedSlot((prev) => (prev === null || prev < slotsRef.current.length ? prev : null));
  }, [actorId, setSelectedSlot]);
}

/**
 * @param {object} args - roster, setRoster, actor, actorId, setActorId, setSelectedSlot.
 */
export default function useShellAttackProfiles({ roster, setRoster, actor, actorId, setActorId, setSelectedSlot }) {
  const [scratchSlots, setScratchSlots] = useState(blankProfile);
  const atkProfiles = useMemo(
    () => toLegacyAtkProfiles(roster, actorId, scratchSlots),
    [roster, actorId, scratchSlots],
  );
  const setAtkProfiles = useCallback(
    (updater) => {
      if (!actorId) setScratchSlots((prevScratch) => nextScratch(roster, prevScratch, updater));
      else setRoster((prev) => nextRoster(prev, actorId, scratchSlots, updater));
    },
    [actorId, roster, scratchSlots, setRoster],
  );
  const importAtkProfiles = useImportAtkProfiles(setRoster, setActorId);
  const currentSlots = useMemo(() => (actor ? projectLoadout(actor).slots : scratchSlots), [actor, scratchSlots]);
  useDropStaleSelection(actorId, currentSlots, setSelectedSlot);
  return { atkProfiles, localCurrentProfile: atkProfiles.current, setAtkProfiles, importAtkProfiles, currentSlots };
}
